import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useInView } from 'react-intersection-observer';
import cx from 'classnames';
import s from './QuizQuestion.module.scss';
import cornerRB from '../../imgs/corner-rb.svg';
import cornerLB from '../../imgs/corner-lb.svg';
import cornerLT from '../../imgs/corner-lt.svg';
import cornerRT from '../../imgs/corner-rt.svg';

const propTypes = {
  currentQuestion: PropTypes.shape({
    displayTitle: PropTypes.string,
    QuizDifficultyLevel: PropTypes.oneOf(['Easy', 'Medium', 'Difficult']),
    correctAnswers: PropTypes.arrayOf(PropTypes.string),
    incorrectAnswers: PropTypes.arrayOf(PropTypes.string),
  }),
  totalQuestions: PropTypes.number,
  questionNumber: PropTypes.number,
  onAnswer: PropTypes.func,
  onNextQuestion: PropTypes.func,
  timeIsUp: PropTypes.bool,
};

const QuizQuestion = ({
  currentQuestion = {},
  totalQuestions = null,
  questionNumber = null,
  onNextQuestion = () => {},
  onAnswer = () => {},
  timeIsUp = false,
}) => {
  const [showAnswers, setShowAnswers] = useState(false);
  const [pickedCorrectAnswer, setPickedCorrectAnswer] = useState(false);
  const [pickedIndex, setPickedIndex] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [pausedForResult, setPausedForResult] = useState(false);

  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const handleAnswerClick = (answer, isCorrect, index) => {
    onAnswer(currentQuestion, answer);
    setShowAnswers(true);
    setPickedCorrectAnswer(isCorrect);
    setPickedIndex(index);
    setPausedForResult(true);
  };

  useEffect(() => {
    if (timeIsUp) {
      setShowAnswers(true);
    } else {
      setShowAnswers(false);
    }
  }, [timeIsUp]);

  useEffect(() => {
    const answerClickPause = setTimeout(() => {
      if (pausedForResult) {
        setPausedForResult(false);
        onNextQuestion();
      }
    }, 1500);
    return () => {
      clearTimeout(answerClickPause);
    };
  }, [onNextQuestion, pausedForResult]);

  useEffect(() => {
    if (currentQuestion.correctAnswers && currentQuestion.incorrectAnswers) {
      // combine all answers and shuffle randomly
      const combinedAnswers = [];
      currentQuestion.correctAnswers.forEach((text) => {
        combinedAnswers.push({
          text,
          isCorrect: true,
        });
      });

      currentQuestion.incorrectAnswers.forEach((text) => {
        combinedAnswers.push({
          text,
          isCorrect: false,
        });
      });
      setAnswers(combinedAnswers.sort(() => 0.5 - Math.random()));
    }
  }, [currentQuestion]);

  return (
    <div
      ref={ref}
      className={s.root}
      style={{
        '--cornerRB': `url(${cornerRB})`,
        '--cornerRT': `url(${cornerRT})`,
        '--cornerLB': `url(${cornerLB})`,
        '--cornerLT': `url(${cornerLT})`,
      }}
    >
      <div className={s.questionNumber}>{`QUESTION ${questionNumber}/${totalQuestions}`}</div>
      <div className={s.qna}>
        <div className={cx(s.questionContainer, inView ? s.inView : '')}>
          {currentQuestion.displayTitle}
        </div>
        <div className={s.answerContainer}>
          {inView &&
            answers.length > 0 &&
            answers.map((answer, index) => {
              return (
                <button
                  key={answer.text}
                  type="button"
                  className={cx(
                    s.quizButton,
                    showAnswers ? s[`answer${answer.isCorrect}`] : '',
                    pickedCorrectAnswer ? s.correct : '',
                    timeIsUp ? s.timeIsUp : '',
                    pickedIndex === index ? s.picked : '',
                  )}
                  tabIndex="-1"
                  name={answer.text}
                  onClick={() => handleAnswerClick(answer.text, answer.isCorrect, index)}
                  disabled={showAnswers || timeIsUp}
                >
                  {answer.text}
                  <div className={s.icons}>
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 22 22"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className={s.x}
                    >
                      <path
                        d="M4.31703 20.3284L11 13.5588L17.683 20.3284L18.0157 20.6654L18.3484 20.3284L20.3327 18.3184L20.6569 17.9899L20.3327 17.6615L13.6413 10.8833L20.1023 4.3385L20.4265 4.01006L20.1023 3.68162L18.118 1.67156L17.7853 1.33455L17.4526 1.67156L11 8.20779L4.54744 1.67156L4.21474 1.33455L3.88205 1.67156L1.89772 3.68162L1.57348 4.01006L1.89772 4.3385L8.35874 10.8833L1.6673 17.6615L1.34307 17.9899L1.6673 18.3184L3.65163 20.3284L3.98433 20.6654L4.31703 20.3284Z"
                        fill="white"
                        stroke="white"
                        strokeWidth="0.935003"
                      />
                    </svg>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24.78"
                      height="20.43"
                      viewBox="0 0 24.78 20.43"
                      className={s.check}
                      fill="none"
                    >
                      <polygon
                        fill="white"
                        points="9.22 20.43 0 9.2 3.86 6.03 9.42 12.79 21.08 0 24.78 3.37 9.22 20.43"
                      />
                    </svg>
                  </div>
                </button>
              );
            })}
        </div>
      </div>
    </div>
  );
};

QuizQuestion.propTypes = propTypes;
export default QuizQuestion;
